import { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { theme, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { tableHeadRow, ASSET_KINDS, ASSET_SOURCE_LABELS, labelOf, fmtDateTime, SeverityPill, StatusPill, secondaryBtn, type Incident } from "./shared";

// Cyber incidents cross-referenced with the asset register: the assets a
// Sentinel incident, a Defender alert or the typed text names, proposed while an
// incident is recorded (POST /resilience/incidents/suggest-assets); the linked
// assets with their source facts on the incident detail (GET
// /resilience/incidents/:id); and the "Raise incident" action the connector
// pages offer per source record. Contract: docs/OPERATIONAL-RESILIENCE.md.

// ── Types ─────────────────────────────────────────────────────────────────────

export type IncidentSourceKind = "sentinel" | "m365_alert";

/** The source record an incident is being raised from (`?sentinel=` / `?alert=` on the incidents page). */
export interface IncidentSourceRef { kind: IncidentSourceKind; id: string }

export interface AssetSuggestion {
  assetId: string;
  reason: string;
  confidence: "high" | "medium";
  name: string;
  kind: string;
  source: string;
  criticality: string;
}

export interface IncidentPrefill {
  title: string;
  description: string;
  category: string;
  severity: string;
  detectedAt: string;
  occurredAt: string | null;
  attackVector: string | null;
}

export interface SuggestResponse {
  suggestions: AssetSuggestion[];
  source: {
    kind: IncidentSourceKind; id: string; reference: string; title: string; severity: string; status: string; occurredAt: string;
    existingIncident: { id: string; displayId: number | null; title: string } | null;
  } | null;
  prefill: IncidentPrefill | null;
}

export interface LinkedAsset {
  id: string;
  name: string;
  kind: string;
  criticality: string;
  status: "active" | "retired";
  isEssentialFunction: boolean;
  source: string;
  externalId: string | null;
  lastSeenAt: string | null;
  reviewedAt: string | null;
  ownerName: string | null;
  owner: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
  site: { id: string; name: string } | null;
  complianceState: string | null;
  facts: string[];
}

export interface IncidentDetail extends Omit<Incident, "assets"> {
  assets: Array<{ asset: LinkedAsset }>;
  sentinelIncident: { id: string; incidentId: string; incidentNumber: number | null; title: string; status: string; severity: string; occurredAt: string; closedAt: string | null } | null;
  m365Alert: { id: string; alertId: string; title: string; status: string; severity: string; category: string | null; productName: string | null; occurredAt: string } | null;
}

export interface SourceLink { id: string; displayId: number | null; title: string; status: string; sentinelIncidentId: string | null; m365AlertId: string | null }

const incidentRef = (i: { displayId: number | null }) => (i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")}` : "INC");
const sourceLabel = (source: string) => (source === "manual" ? "Manual" : ASSET_SOURCE_LABELS[source] ?? source);

/** The request-body fields that name a source record. */
export const sourceBody = (s: IncidentSourceRef | null | undefined) => (s ? (s.kind === "sentinel" ? { sentinelIncidentId: s.id } : { m365AlertId: s.id }) : {});

/** The source an existing incident was raised from, if any. */
export const sourceOf = (i: Pick<Incident, "sentinelIncidentId" | "m365AlertId">): IncidentSourceRef | null =>
  i.sentinelIncidentId ? { kind: "sentinel", id: i.sentinelIncidentId } : i.m365AlertId ? { kind: "m365_alert", id: i.m365AlertId } : null;

// ── Suggestions while recording ───────────────────────────────────────────────

/**
 * Asset proposals for the incident form, re-fetched 500 ms after the title or
 * description last changed (at once when a source record is named). The first
 * answer for a source also carries the pre-filled record.
 */
export function useAssetSuggestions(args: { title: string; description: string; source: IncidentSourceRef | null; enabled: boolean }) {
  const [state, setState] = useState<{ suggestions: AssetSuggestion[]; response: SuggestResponse | null; loading: boolean; error: string | null }>({ suggestions: [], response: null, loading: false, error: null });
  const seq = useRef(0);
  const sourceKind = args.source?.kind ?? null;
  const sourceId = args.source?.id ?? null;
  useEffect(() => {
    if (!args.enabled) return;
    const hasText = args.title.trim().length >= 3 || args.description.trim().length >= 3;
    if (!hasText && !sourceId) { setState((s) => (s.suggestions.length ? { ...s, suggestions: [] } : s)); return; }
    const id = ++seq.current;
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const d = await api.post<SuggestResponse>("/resilience/incidents/suggest-assets", {
          title: args.title.trim() || null, description: args.description.trim() || null,
          ...sourceBody(sourceKind && sourceId ? { kind: sourceKind, id: sourceId } : null),
        });
        if (id === seq.current) setState({ suggestions: d.suggestions, response: d, loading: false, error: null });
      } catch (e) {
        if (id === seq.current) setState((s) => ({ ...s, loading: false, error: apiErrorMessage(e, "Could not suggest assets") }));
      }
    }, sourceId && !hasText ? 0 : 500);
    return () => clearTimeout(timer);
  }, [args.title, args.description, sourceKind, sourceId, args.enabled]);
  return state;
}

/** The proposals not yet in the selection, each one click from being linked. */
export function SuggestedAssets({ suggestions, selected, loading, error, onAdd }: { suggestions: AssetSuggestion[]; selected: string[]; loading: boolean; error: string | null; onAdd: (ids: string[]) => void }) {
  const pending = suggestions.filter((s) => !selected.includes(s.assetId));
  if (!loading && !error && pending.length === 0) return null;
  return (
    <div style={{ gridColumn: "1 / -1", display: "grid", gap: 6, padding: "10px 12px", borderRadius: 3, background: theme.color.panelAlt }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <span style={text.eyebrow}>Suggested assets{loading ? " …" : ""}</span>
        {pending.length > 1 && <button type="button" style={secondaryBtn} onClick={() => onAdd(pending.map((s) => s.assetId))}>Add all</button>}
      </div>
      {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
      {pending.map((s) => (
        <div key={s.assetId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" onClick={() => onAdd([s.assetId])} title={`Link ${s.name}`} style={{ ...secondaryBtn, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4, borderStyle: s.confidence === "high" ? "solid" : "dashed" }}>
            <Plus size={12} /> Add
          </button>
          <div style={{ ...text.hint, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>{s.name}</span>
            <span style={{ color: theme.color.faint, marginLeft: 6 }}>{labelOf(ASSET_KINDS, s.kind)} · {sourceLabel(s.source)} · {s.confidence === "high" ? "named in the incident" : "likely"}</span>
            <div style={text.meta}>{s.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── The linked assets on an incident ──────────────────────────────────────────

const COMPLIANCE_COLOUR: Record<string, string> = { compliant: theme.color.successText, noncompliant: theme.color.dangerText, inGracePeriod: theme.color.warningText, conflict: theme.color.warningText, error: theme.color.warningText };

/** A small label for where an incident came from (list rows and the detail header). */
export function IncidentSourceTag({ incident }: { incident: Pick<Incident, "sentinelIncidentId" | "m365AlertId"> }) {
  const source = sourceOf(incident);
  if (!source) return null;
  return <span style={{ fontSize: 10, fontWeight: 600, color: theme.color.faint, whiteSpace: "nowrap" }} title={source.kind === "sentinel" ? "Raised from a Microsoft Sentinel incident" : "Raised from a Microsoft Defender alert"}>{source.kind === "sentinel" ? "Sentinel" : "Defender"}</span>;
}

/**
 * The incident detail's "Assets affected" section: every linked asset with its
 * source, Intune compliance state, owner and what the source last reported, plus
 * the source record the incident was raised from. `refreshKey` re-reads it.
 */
export function LinkedAssetsPanel({ incidentId, refreshKey }: { incidentId: string; refreshKey?: unknown }) {
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.get<{ incident: IncidentDetail }>(`/resilience/incidents/${incidentId}`)
      .then((d) => { if (live) { setDetail(d.incident); setError(null); } })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Could not load the linked assets")); });
    return () => { live = false; };
  }, [incidentId, refreshKey]);

  if (error) return <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>;
  if (!detail) return <div style={text.hint}>Loading assets…</div>;
  const raised = detail.sentinelIncident
    ? { label: `Microsoft Sentinel incident${detail.sentinelIncident.incidentNumber != null ? ` #${detail.sentinelIncident.incidentNumber}` : ""}`, title: detail.sentinelIncident.title, severity: detail.sentinelIncident.severity, status: detail.sentinelIncident.status, at: detail.sentinelIncident.occurredAt, to: "/connections?tab=sentinel" }
    : detail.m365Alert
      ? { label: `${detail.m365Alert.productName || "Microsoft Defender"} alert`, title: detail.m365Alert.title, severity: detail.m365Alert.severity, status: detail.m365Alert.status, at: detail.m365Alert.occurredAt, to: "/connections?tab=m365security" }
      : null;
  return (
    <section>
      <h3 style={{ ...text.sectionTitle, margin: "0 0 6px" }}>Assets affected</h3>
      {raised && (
        <div style={{ ...text.hint, marginBottom: 10 }}>
          Raised from <PortalLink to={raised.to} style={{ color: "var(--c-accentText)" }}>{raised.label}</PortalLink>: “{raised.title}” ({raised.severity}, {raised.status}, {fmtDateTime(raised.at)}).
        </div>
      )}
      {detail.assets.length === 0 ? (
        <div style={{ ...text.body, color: theme.color.faint }}>No assets linked. Edit the incident to add the ones it hit — the register proposes them from the text.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ ...text.hint, width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={tableHeadRow}>
                {["Asset", "Type", "Criticality", "Source", "State", "Owner"].map((h) => <th key={h} style={{ padding: "6px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {detail.assets.map(({ asset: a }) => {
                const ownerInactive = a.owner ? !a.owner.isActive : false;
                return (
                  <tr key={a.id} style={{ borderBottom: "1px solid var(--c-border)", verticalAlign: "top" }}>
                    <td style={{ padding: "8px 10px", color: theme.color.text, fontWeight: 600 }}>
                      <PortalLink to={`/resilience/assets?asset=${a.id}`} style={{ color: theme.color.text, textDecoration: "none" }}>{a.name}</PortalLink>
                      {a.isEssentialFunction && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "var(--c-accentText)" }}>ESSENTIAL</span>}
                      {a.status === "retired" && <span style={{ marginLeft: 6 }}><StatusPill value="retired" /></span>}
                      {a.facts.length > 0 && <div style={{ ...text.meta, fontWeight: 400 }}>{a.facts.join(" · ")}</div>}
                    </td>
                    <td style={{ padding: "8px 10px", color: theme.color.muted, whiteSpace: "nowrap" }}>{labelOf(ASSET_KINDS, a.kind)}</td>
                    <td style={{ padding: "8px 10px" }}><SeverityPill value={a.criticality} /></td>
                    <td style={{ padding: "8px 10px", color: theme.color.muted, whiteSpace: "nowrap" }} title={a.lastSeenAt ? `Last seen ${fmtDateTime(a.lastSeenAt)}` : undefined}>
                      {sourceLabel(a.source)}
                      {a.source !== "manual" && !a.reviewedAt && a.status === "active" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 2, background: theme.color.warningBg, color: theme.color.warningText }}>NEEDS REVIEW</span>}
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", color: a.complianceState ? COMPLIANCE_COLOUR[a.complianceState] ?? theme.color.muted : theme.color.faint }}>
                      {a.complianceState ? `Intune: ${a.complianceState}` : "—"}
                    </td>
                    <td style={{ padding: "8px 10px", color: ownerInactive ? theme.color.dangerText : theme.color.muted }}>
                      {a.owner?.displayName || a.owner?.email || a.ownerName || "—"}{ownerInactive && <span style={{ ...text.meta, color: "inherit", marginLeft: 4 }}>(inactive)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── "Raise incident" on the connector pages ───────────────────────────────────

/**
 * Which synced Sentinel incidents / Defender alerts already have a register
 * incident, and whether the member may raise one. Fetched once per page while
 * `enabled`; `reload` after the page re-syncs.
 */
export function useIncidentSourceLinks(enabled: boolean) {
  const [links, setLinks] = useState<SourceLink[]>([]);
  const [canRaise, setCanRaise] = useState(false);
  const reload = useCallback(async () => {
    try {
      const d = await api.get<{ links: SourceLink[]; canRaise: boolean }>("/resilience/incidents/source-links");
      setLinks(d.links); setCanRaise(d.canRaise);
    } catch { /* the column then shows nothing */ }
  }, []);
  useEffect(() => { if (enabled) void reload(); }, [enabled, reload]);
  return { links, canRaise, reload };
}

/** A table cell: the incident already raised from this source record, or the action to raise one. */
export function RaiseIncidentCell({ kind, sourceId, links, canRaise }: { kind: IncidentSourceKind; sourceId: string; links: SourceLink[]; canRaise: boolean }) {
  const link = links.find((l) => (kind === "sentinel" ? l.sentinelIncidentId === sourceId : l.m365AlertId === sourceId));
  if (link) {
    return (
      <PortalLink to={`/resilience/incidents?incident=${encodeURIComponent(link.id)}`} title={`${link.title} (${link.status})`} style={{ ...text.ref, color: "var(--c-accentText)", whiteSpace: "nowrap" }}>
        {incidentRef(link)}
      </PortalLink>
    );
  }
  if (!canRaise) return <span style={{ color: theme.color.faint }}>—</span>;
  return (
    <PortalLink to={`/resilience/incidents?${kind === "sentinel" ? "sentinel" : "alert"}=${encodeURIComponent(sourceId)}`} title="Open the cyber incident register with this record filled in and the assets it names proposed" style={{ ...secondaryBtn, textDecoration: "none", display: "inline-block", padding: "4px 10px" }}>
      Raise incident
    </PortalLink>
  );
}
